import { reactive } from 'vue';
import { apprenticeApi } from './api';
import { isModuleActive, type TenantModuleAccess } from './moduleSwitcher';

// Administration › Training occupations (ADR 0092): who may open the page,
// decided in ONE place for the router guard, the sidebar entry and the page
// itself — the sibling of utils/statutoryRulesAccess.ts (ADR 0091), kept as a
// second copy rather than a shared helper (ADR 0001).
//
// Which recognised training occupations a workspace offers is kept by
// apprentice-api, which admits two kinds of people to every
// `/training-occupations` route: the workspace owner or a suite admin (the
// master registry, as user-api reports it — the same set `hasAdminRole` reads
// here; a consented support grant is not one), and a member of the
// apprenticeship module holding its `manage` capability (the head of training).
// The first this app knows from the tenant list. The second it cannot know —
// the module's roles are the module's — so it asks apprentice-api
// (`GET /training-occupations/access`) and keeps the answer per user and tenant
// for the session: the sidebar must not ask again on every render.

/** The page. Fixed, so a link from elsewhere (a notice, the module's own settings) can rely on it. */
export const TRAINING_OCCUPATIONS_PATH = '/admin/training-occupations';

/** apprentice-api's answer for an admitted caller. */
export interface TrainingOccupationsAccess {
  canManage: true;
  via: 'workspace_admin' | 'apprentice_manage';
}

export type TrainingOccupationsAccessAnswer =
  | { state: 'granted'; access: TrainingOccupationsAccess }
  /** 403: neither the workspace admin nor a `manage` holder of the apprenticeship module. */
  | { state: 'refused' }
  /** 404: the workspace has no active apprenticeship module, so there is no offer to keep. */
  | { state: 'moduleInactive' }
  /** Anything else (unreachable, provisioning, a malformed answer): not kept, asked again next time. */
  | { state: 'unavailable'; error: unknown };

/** Whether the workspace runs the apprenticeship module — without it nobody trains anybody here. */
export function tenantRunsApprenticeship(tenant: TenantModuleAccess | null | undefined): boolean {
  return !!tenant && isModuleActive(tenant, 'apprenticeship');
}

// Settled answers, per `user|tenant`. Reactive, so the sidebar shows the entry
// the moment a probe it started comes back. An answer is the user's in that
// tenant: another account in this tab, or another tenant, is another key.
const answers = reactive(new Map<string, TrainingOccupationsAccessAnswer>());
const inflight = new Map<string, Promise<TrainingOccupationsAccessAnswer>>();
// Bumped by `forgetTrainingOccupationsAccess`: a probe that started before it
// must not write its answer back into the emptied cache.
let generation = 0;

const keyOf = (userId: string, tenantSlug: string) => `${userId}|${tenantSlug}`;

function readAnswer(data: unknown): TrainingOccupationsAccessAnswer {
  const body = data as Partial<TrainingOccupationsAccess> | null;
  // A 200 that is not the answer (a proxy's stub) admits nobody.
  if (!body || body.canManage !== true) {
    return { state: 'unavailable', error: new Error('GET /training-occupations/access did not answer an access') };
  }
  return {
    state: 'granted',
    access: {
      canManage: true,
      via: body.via === 'apprentice_manage' ? 'apprentice_manage' : 'workspace_admin',
    },
  };
}

/** The answer this session already holds for the user in that tenant, if any. */
export function cachedTrainingOccupationsAccess(userId: string | null | undefined, tenantSlug: string | null | undefined) {
  if (!userId || !tenantSlug) return undefined;
  return answers.get(keyOf(userId, tenantSlug));
}

/**
 * Ask apprentice-api whether the user may manage the training occupations the
 * tenant offers. A settled answer (granted, refused, module inactive) is kept
 * for the session; concurrent asks share one request. Never throws. In a demo
 * it is asked as the persona worn, and a persona without a seat there is a
 * refusal like any other — never a reason to take the persona off (issue 807).
 */
export function probeTrainingOccupationsAccess(
  userId: string | null | undefined,
  tenantSlug: string | null | undefined,
): Promise<TrainingOccupationsAccessAnswer> {
  if (!userId || !tenantSlug) return Promise.resolve({ state: 'refused' });
  const key = keyOf(userId, tenantSlug);
  const known = answers.get(key);
  if (known) return Promise.resolve(known);
  const pending = inflight.get(key);
  if (pending) return pending;
  const startedIn = generation;
  const request = apprenticeApi
    .get('/training-occupations/access', { headers: { 'X-Tenant-Slug': tenantSlug }, suppressGlobalError: true, keepDemoPersona: true })
    .then(
      ({ data }) => readAnswer(data),
      (error: unknown): TrainingOccupationsAccessAnswer => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 403) return { state: 'refused' };
        if (status === 404) return { state: 'moduleInactive' };
        return { state: 'unavailable', error };
      },
    )
    .then((answer) => {
      if (startedIn !== generation) return answer;
      inflight.delete(key);
      if (answer.state !== 'unavailable') answers.set(key, answer);
      return answer;
    });
  inflight.set(key, request);
  return request;
}

/** The workspace admin facts the decision reads (the tenants store's). */
export interface TrainingOccupationsViewer {
  userId: string | null | undefined;
  tenantSlug: string | null | undefined;
  /** Owner or suite admin (`tenantsStore.hasAdminRole`). */
  isWorkspaceAdmin: boolean;
  /** Whether the tenant runs the apprenticeship module (`tenantRunsApprenticeship`). */
  runsApprenticeship: boolean;
}

/**
 * The route guard's decision. A workspace admin passes without a request (the
 * page then says so if the module is not active); anybody else passes only on
 * apprentice-api's 200 — fail-closed: no module, a refusal or no answer at all
 * keeps them out, and apprentice-api decides again on every request behind the
 * page.
 */
export async function mayOpenTrainingOccupations(viewer: TrainingOccupationsViewer): Promise<boolean> {
  if (viewer.isWorkspaceAdmin) return true;
  if (!viewer.runsApprenticeship) return false;
  const answer = await probeTrainingOccupationsAccess(viewer.userId, viewer.tenantSlug);
  return answer.state === 'granted';
}

/**
 * The sidebar's decision, synchronous: only for a tenant that runs the
 * apprenticeship module, then for the workspace admin, or for whoever this
 * session's probe admitted. Use `probeTrainingOccupationsAccess` to learn the
 * latter; never probe without the module.
 */
export function showsTrainingOccupationsNav(viewer: TrainingOccupationsViewer): boolean {
  if (!viewer.runsApprenticeship) return false;
  if (viewer.isWorkspaceAdmin) return true;
  return cachedTrainingOccupationsAccess(viewer.userId, viewer.tenantSlug)?.state === 'granted';
}

/**
 * Drop every kept answer: on sign-out, and whenever the tenants store asks the
 * workspace to re-read itself (a tenant switch, the membership list re-read, the
 * end of setup) — a role given or taken in the meantime must count.
 */
export function forgetTrainingOccupationsAccess(): void {
  generation += 1;
  answers.clear();
  inflight.clear();
}
